// ── Toolbar Management ──────────────────────────────────────────────
// Mode switching, toolbar preset updates, status bar

import { state } from './state.ts';
import { $, $$ } from './canvas.ts';
import { getArcData, findClosestArc } from '../calculations/logic.ts';
import { buildModelSelect, populateArcSelect, renderTemp } from './render.ts';
import { t } from '../shared/i18n.ts';
import { convertFlow, convertLength, convertSmallLength, getUnitLabels, formatUnit } from '../shared/units.ts';

export function updateToolbarPreset() {
  const arc = findClosestArc(state.activeModel, state.activeArc);
  state.activeArc = arc;
  const arcData = getArcData(state.activeModel, arc);

  buildModelSelect($("#activeModel"), state.activeModel);
  populateArcSelect($("#activeArc") as HTMLSelectElement, state.activeModel, arc);

  // Follow the user's `preferences.units` (hydrated into state.userUnits at
  // boot), like every other readout in the planner. This used to call the
  // since-removed getUnitSystem(getCurrentLanguage()), which derived the system
  // from the UI language — English meant imperial regardless of the stored
  // preference, so an English-speaking metric user read "r=9.8 ft 0.20 GPM"
  // in the toolbar while the sidebar showed "r=3.0 m 0.76 L/min" for the very
  // same nozzle (and a Ukrainian user who chose imperial got the mirror image).
  const sys = state.userUnits;
  const labels = getUnitLabels(sys);
  const r = formatUnit(convertLength(arcData.radius, sys));
  const f = formatUnit(convertFlow(arcData.flow, sys));
  $("#toolbarPresetInfo").textContent = `r=${r}${labels.length} ${f}${labels.flow}`;
}

export function setMode(mode: string) {
  state.mode = mode;
  state.tempBoundary = [];
  state.tempZone = [];
  state.tempPipe = [];
  state.tempDripLine = [];

  const sprinklerGroup = $("#sprinklerToolbarGroup");
  const pipeGroup = $("#pipeToolbarGroup");
  const objectGroup = $("#objectToolbarGroup");
  const dripGroup = $("#dripToolbarGroup");
  if (sprinklerGroup) sprinklerGroup.hidden = mode !== "sprinkler";
  if (pipeGroup) pipeGroup.hidden = mode !== "pipe";
  if (objectGroup) objectGroup.hidden = mode !== "tree";
  if (dripGroup) dripGroup.hidden = mode !== "drip";

  $$(".tool-btn").forEach((btn) => {
    // @ts-expect-error — TS strict
    btn.classList.toggle("active", btn.dataset.mode === mode);
  });
  const cont = $("#canvasContainer");
  cont.setAttribute("data-mode", mode);

  const modeName = t(`modes.${mode}`) || mode;
  let hint = t(`hints.${mode}`) || "";
  if (mode === "sprinkler") {
    hint = t("hints.sprinkler", { model: state.activeModel, arc: state.activeArc });
  } else if (mode === "pipe") {
    const diameter = convertSmallLength(state.activePipeDiameter, state.userUnits);
    const shown = state.userUnits === "imperial" ? diameter.toFixed(2) : String(diameter);
    hint = t("hints.pipe", { diameter: `${shown} ${getUnitLabels(state.userUnits).smallLength}` });
  } else if (mode === "drip") {
    hint = t("hints.drip", { model: state.activeDripModel });
  }

  $("#statusMode").textContent = `${t("status.modePrefix")}${modeName}`;
  $("#statusHint").textContent = hint;
  renderTemp();
}
